/**
 * 取景框 → 成片裁切（**纯函数，不碰 DOM，可单测**）
 * -----------------------------------------------------------------------------
 * 为什么需要它：蒙版只负责「把人摆对位置」，但如果成片仍是整帧，上传的数据量
 * 会翻几倍。所以仍要按框裁 —— 但**只裁，不再定尺寸**（见下面的口径变更）。
 *
 * 三条口径（`06-身份核验.md` §4.1）：
 *   - **证件照**：按框**外扩一圈**再裁，保留原生像素 —— 框只是构图引导，
 *     成品的 67% 头高由**服务端**按人脸关键点重裁（见文件末尾的变更说明）
 *   - **身份证**：按框**外扩**一圈再裁 —— 框是给人对齐用的，四角一旦被切掉，
 *     OCR 的 `card_quad` 检测就失败（宁可多带背景，不可缺角）
 *   - **无框**：整帧，不裁
 *
 * `object-fit: cover` 是这里唯一的坑：video 元素比例（屏幕）与视频内容比例
 * （摄像头）几乎不会相等，cover 会把内容按「较大的那一维」放大后居中裁掉溢出的
 * 部分。所以「屏幕坐标 ÷ 元素尺寸」的线性换算**不成立**，必须先求出缩放比与偏移。
 *
 * 不使用镜像：预览没有 `transform: scaleX(-1)`，成片与所见一致，无需翻折。
 *
 * ── 变更说明（2026-10-21）：证件照为什么不再按框硬裁成一寸 ──────────────
 * 旧口径是「框 = 成片边界，裁完直接标准化成 295×413」。这在**服务端只存档、
 * 不动像素**的前提下没问题，但接上真实生成链之后必然出错：
 *
 *   证件照的硬指标是「**发顶 → 下颚 = 画幅高的 67%**」。要算出这句话，服务端
 *   必须同时知道**发顶**和**下颚**两处的坐标。而「发顶」在框内一旦被切掉，
 *   服务端拿到的图上就**没有头顶**了 —— 它会把 image 上沿当成发顶，
 *   算出来的头高偏小、成片头偏大，且完全不报错。
 *
 * 所以职责重新划分：
 *   - **前端**：只保证「头肩在画面里、分辨率够」，按框外扩 30% 裁，保留原生像素
 *   - **服务端**：拿整段画面 → 检测下颚 + 分割取发顶 → 按 67% 重新构图 → 换底 → 295×413
 *
 * `bleed: 0.30` 的取值理由：框自身的顶部留白是 10%（见 `PortraitMask`），
 * 再往外留 30% 足够兜住「头放得稍高、发梢出框」的常见偏差；再大就会让
 * `fitIntoBounds` 把裁切区域等比缩小（框撑满了画面时必然触发），
 * 反而把头上的有效像素数压下来。
 *
 * 任务卡：T-08（身份核验链）· 证件照生成
 */

/** 视口坐标里的矩形（`getBoundingClientRect()` 的四个字段） */
export interface CaptureRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 尺寸（只关心两个字段，便于把 video 元素矩形与像素尺寸统一传参） */
export interface Size {
  width: number;
  height: number;
}

/** 取景蒙版种类（与 `CameraCapture` 的 `mask` 一一对应） */
export type CaptureMask = 'id-front' | 'id-back' | 'portrait';

// ============================================================ 一寸规格

const MM_PER_INCH = 25.4;

/**
 * 一寸证件照规格。
 *
 * ⚠️ **偏差记录**：按口径「尺寸规格由后台配置下发」，这三个数应当来自服务端配置。
 * 一期没有对应的配置项（`admission_thresholds` 只管通过线），因此**集中在这一处**
 * 硬编码，不散落到组件里 —— 将来接配置时只改这里，不必再全仓找数字。
 */
export const ID_PHOTO_SPEC = {
  widthMm: 25,
  heightMm: 35,
  dpi: 300,
} as const;

/** 一寸 @300dpi = 295×413 */
export const ID_PHOTO_PIXEL_WIDTH = Math.round(
  (ID_PHOTO_SPEC.widthMm / MM_PER_INCH) * ID_PHOTO_SPEC.dpi,
);
export const ID_PHOTO_PIXEL_HEIGHT = Math.round(
  (ID_PHOTO_SPEC.heightMm / MM_PER_INCH) * ID_PHOTO_SPEC.dpi,
);

/**
 * ⚠️ 这两个像素值**不再用于前端裁切**，只作为「目标规格」的书面口径保留
 * （上传提示文案、与服务端 `kyc.id-photo.ts` 的 `ID_PHOTO_SPEC` 对齐核查）。
 * 前端输出的是**原生分辨率**的裁切结果 —— 缩放会损失锐度，而缩放后服务端
 * 还要按 67% 头高再裁一次，等于两次重采样。
 */

// ============================================================ 裁切规则

export interface CropRule {
  /**
   * 框内缩像素（去掉框自身的边框线）。
   * 框是 `border: 2px solid`，`getBoundingClientRect()` 把边框算在内，
   * 不内缩就会把边框那一圈像素也带进成片。
   */
  inset: number;
  /**
   * 框外扩比例：`0.12` = 四周各多带框尺寸的 12%。
   * 只给身份证用 —— 见文件头的口径说明。
   */
  bleed: number;
  /** 固定输出像素；不给则保持裁切出的原始像素（不缩放） */
  output?: Size;
}

/**
 * 三种框各自的裁切规则。
 *
 * 三种框**都不再给 `output`** —— 一律保留裁切出的原生像素，缩放交给服务端一次做完。
 * 证件照的 `bleed` 必须大于 0，理由见文件头的「变更说明」：头顶一旦出框，
 * 服务端就再也算不出发顶。
 */
export const CAPTURE_CROP_RULE: Record<CaptureMask, CropRule> = {
  portrait: { inset: 0, bleed: 0.3 },
  'id-front': { inset: 2, bleed: 0.12 },
  'id-back': { inset: 2, bleed: 0.12 },
};

// ============================================================ 坐标换算

export interface FrameCropInput {
  /** 取景框在视口里的矩形（含边框） */
  frame: CaptureRect;
  /** video 元素在视口里的矩形 */
  video: CaptureRect;
  /** `video.videoWidth` / `video.videoHeight` */
  videoSize: Size;
}

/**
 * 视口矩形 → video 内部像素矩形（**按 cover 修正**）。
 *
 * 推导：
 *   `scale  = max(元素宽 / 视频宽, 元素高 / 视频高)`  —— cover 取较大的那一维
 *   `offset = (元素尺寸 - 视频尺寸 × scale) / 2`      —— 溢出部分居中裁掉，故为负
 *   `像素坐标 = (视口坐标 - 元素起点 - offset) / scale`
 */
export function mapFrameToVideoCrop(input: FrameCropInput): CaptureRect {
  const { frame, video, videoSize } = input;
  const { width: videoWidth, height: videoHeight } = videoSize;

  // 取不到真实尺寸时（视频还没出第一帧）退回整帧，不要把画面裁成 0×0
  if (videoWidth <= 0 || videoHeight <= 0 || video.width <= 0 || video.height <= 0) {
    return { x: 0, y: 0, width: Math.max(videoWidth, 1), height: Math.max(videoHeight, 1) };
  }

  const scale = Math.max(video.width / videoWidth, video.height / videoHeight);
  const offsetX = (video.width - videoWidth * scale) / 2;
  const offsetY = (video.height - videoHeight * scale) / 2;

  const width = frame.width / scale;
  const height = frame.height / scale;
  const x = (frame.x - video.x - offsetX) / scale;
  const y = (frame.y - video.y - offsetY) / scale;

  return fitIntoBounds({ x, y, width, height }, { width: videoWidth, height: videoHeight });
}

/**
 * 按规则调整矩形：先内缩（去边框），再外扩（留余量），最后仍走一遍
 * 「等比收缩 + 平移入画」—— 外扩可能把框推出画面外。
 */
export function applyCropRule(crop: CaptureRect, rule: CropRule, videoSize: Size): CaptureRect {
  const inset = rule.inset * 2;
  let width = Math.max(crop.width - inset, 1);
  let height = Math.max(crop.height - inset, 1);
  let x = crop.x + rule.inset;
  let y = crop.y + rule.inset;

  if (rule.bleed > 0) {
    const growX = width * rule.bleed;
    const growY = height * rule.bleed;
    width += growX * 2;
    height += growY * 2;
    x -= growX;
    y -= growY;
  }

  return fitIntoBounds({ x, y, width, height }, videoSize);
}

/** 成片输出尺寸：有固定目标就用目标（标准化），否则保持裁切出的原始像素 */
export function resolveCropOutput(crop: CaptureRect, rule: CropRule): Size {
  if (rule.output) return { width: rule.output.width, height: rule.output.height };
  return { width: crop.width, height: crop.height };
}

/**
 * 把矩形塞进画面内，**保持宽高比例**：
 *   ① 超出画面 → 以中心为基准等比缩小（直接截断会改变 25:35 的比例）
 *   ② 越界 → 只平移、不动尺寸（构图不缩水）
 */
function fitIntoBounds(rect: CaptureRect, bounds: Size): CaptureRect {
  let { x, y, width, height } = rect;

  const fitting = Math.min(1, bounds.width / width, bounds.height / height);
  if (fitting < 1) {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    width *= fitting;
    height *= fitting;
    x = centerX - width / 2;
    y = centerY - height / 2;
  }

  x = Math.min(Math.max(x, 0), Math.max(bounds.width - width, 0));
  y = Math.min(Math.max(y, 0), Math.max(bounds.height - height, 0));

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.max(Math.round(width), 1),
    height: Math.max(Math.round(height), 1),
  };
}
